import { useContext, useEffect, useState } from 'react';
import Api, { BASE_ROUTE } from '../../../../api';
import {
    Alert, Button, Container, Pagination,
    Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { useNavigate, useSearchParams, useLoaderData } from 'react-router-dom';
import PostPreview from './PostPreview';
import { IPost } from './interface';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { motion } from 'framer-motion';
import Label, { formatDate, getLabel } from '../../../../utils';
import UserContext from '../../../../context/userContext';
import style from './Post.module.css';

export async function postListLoader() { return {}; }

interface ITag { id: number; tag: string }

export default function PostList() {
    useLoaderData(); // keep for router compatibility
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userData, roles, setSnackbarText } = useContext(UserContext);

    const [paginationInfo, setPaginationInfo] = useState({ total_pages: 0, total_posts: 0 });
    const [page, setPage] = useState(1);
    const [posts, setPosts] = useState<IPost[]>([]);
    const [draftPosts, setDraftPosts] = useState<IPost[]>([]);
    const [showDrafts, setShowDrafts] = useState(false);
    const [tags, setTags] = useState<ITag[]>([]);
    const [selectedTagId, setSelectedTagId] = useState(0);

    async function getPosts(pageNum: number) {
        try {
            const optionalTag = selectedTagId !== 0 ? `&tagId=${selectedTagId}` : '';
            const res: any = await Api.get(`/intranet/posts?page=${pageNum}${optionalTag}`);
            setPaginationInfo(res.pagination);
            setPosts(res.posts as IPost[]);
            setDraftPosts(res.draft_posts);
            setTags(res.tags);
        } catch (error) {
            console.error(error);
            setSnackbarText(getLabel('snackbar.somethingWentWrong', userData?.user?.lang ?? 'no'));
        }
    }

    useEffect(() => { getPosts(page); }, [page]);
    useEffect(() => { getPosts(1); setPage(1); }, [selectedTagId]);

    async function createNewPost() {
        try {
            const res: any = await Api.post('/intranet/posts');
            const post: IPost = res.post as IPost;
            navigate(`${BASE_ROUTE}/posts/${post.id}/edit`);
            setSnackbarText(getLabel('snackbar.createdNewPost', userData?.user?.lang ?? 'no'));
        } catch (error) {
            console.error(error);
            setSnackbarText(getLabel('snackbar.somethingWentWrong', userData?.user?.lang ?? 'no'));
        }
    }

    const featured = posts[0];
    const rest = posts.slice(1);

    return (
        <Container maxWidth={false} className={style.listPage}>
            <motion.header
                className={style.listHead}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
            >
                <h1 className={style.listTitle}>
                    <span className={style.listTitleItalic}>Nyheter</span>
                </h1>
                {roles.isPostEditor && (
                    <Button
                        onClick={createNewPost}
                        startIcon={<AddIcon />}
                        variant='text'
                        className={`themeButton ${style.createCta}`}
                    >
                        <Label k="postList.create" />
                    </Button>
                )}
            </motion.header>

            <div className={style.listRule} />

            {/* Filter pills */}
            <nav className={style.filters} aria-label="Kategorier">
                <button
                    className={`${style.pill} ${selectedTagId === 0 ? style.pillActive : ''}`}
                    onClick={() => setSelectedTagId(0)}
                >
                    Alle
                </button>
                {tags.map((tag) => (
                    <button
                        key={tag.id}
                        className={`${style.pill} ${selectedTagId === tag.id ? style.pillActive : ''}`}
                        onClick={() => setSelectedTagId(tag.id)}
                    >
                        {tag.tag}
                    </button>
                ))}
            </nav>

            {/* Drafts alert */}
            {roles.isPostEditor && draftPosts.length > 0 && (
                <Alert
                    severity="info"
                    className={style.draftBanner}
                    action={
                        <Button color="inherit" size="small" onClick={() => setShowDrafts(!showDrafts)}>
                            <Label k={showDrafts ? 'postList.alertDraftTextCta2' : 'postList.alertDraftTextCta'} />
                        </Button>
                    }
                >
                    <Label k="postList.alertDraftText" />
                </Alert>
            )}

            {/* Content */}
            {showDrafts ? (
                <div className={style.draftsTable}>
                    <Table aria-label="utkast">
                        <TableHead>
                            <TableRow>
                                <TableCell>Opprettet</TableCell>
                                <TableCell>Forfatter</TableCell>
                                <TableCell>Tittel</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {draftPosts.map((post, index) => (
                                <TableRow key={index}>
                                    <TableCell>{formatDate(post.created)}</TableCell>
                                    <TableCell>{post.authors.join(', ')}</TableCell>
                                    <TableCell>{post.title}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            startIcon={<EditIcon />}
                                            className='themeButton'
                                            onClick={() => navigate(`${BASE_ROUTE}/posts/${post.id}/edit`)}
                                        >
                                            Gå til sak
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : posts.length === 0 ? (
                <div className={style.emptyState}>
                    <div className={style.emptyKicker}>Tomt</div>
                    <div className={style.emptyText}>Ingen innlegg i denne kategorien ennå.</div>
                </div>
            ) : (
                <>
                    {featured && (
                        <motion.div
                            className={style.featured}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <div className={style.featuredKicker}>Hovedoppslag</div>
                            <PostPreview post={featured} isLast />
                        </motion.div>
                    )}

                    {rest.length > 0 && (
                        <div className={style.grid}>
                            {rest.map((post, index) => (
                                <motion.div
                                    key={post.id}
                                    className={style.gridItem}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: 0.05 + index * 0.04 }}
                                >
                                    <PostPreview post={post} isLast />
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {paginationInfo.total_pages > 1 && (
                        <div className={style.paginationWrap}>
                            <span className={style.paginationLabel}>
                                Side {page} av {paginationInfo.total_pages}
                            </span>
                            <Pagination
                                count={paginationInfo.total_pages}
                                page={page}
                                onChange={(_, value) => {
                                    setPage(value);
                                    setSearchParams({ page: value.toString() });
                                }}
                                shape="rounded"
                                size="small"
                                className={style.pagination}
                            />
                        </div>
                    )}
                </>
            )}
        </Container>
    );
}
